export const PAGE_TOUR_STORAGE_KEY = "mis.onboarding.pageTour.v1";
export const START_PAGE_TOUR_EVENT = "mis:start-page-tour";
export const STOP_PAGE_TOUR_EVENT = "mis:stop-page-tour";
export const PAGE_TOUR_STEP_EVENT = "mis:page-tour-step";
export const DASHBOARD_TOUR_STORAGE_KEY = "mis.onboarding.dashboardTour.v1";
export const PIVOT_TOUR_VARIANT = "pivot";

const step = (id, title, body, target, fallback) => ({
  id,
  title,
  body,
  target,
  fallback,
});

const PAGE_INTRO = step(
  "intro",
  "This page",
  "The header always shows where you are. Use the sidebar on the left to move between sections.",
  "[data-tour='page-intro']",
  "You are on this page. Use the sidebar to open another section."
);

const TABS = step(
  "tabs",
  "Switch views",
  "These tabs switch the work on this page. Use them to move between creating, listing, or different record types.",
  "[data-tour='tabs']",
  "Tabs appear near the top of this page once it finishes loading."
);

const PROGRAM = step(
  "program",
  "Choose a program",
  "Pick the program this work belongs to. Fields and lists below follow that choice.",
  "[data-tour='program-select']",
  "Select a program when the dropdown appears."
);

const FORM = step(
  "form",
  "Fill the form",
  "Required fields are marked. Save or Submit at the bottom writes the record.",
  "[data-tour='form']",
  "The form sits in the main white card on this page."
);

const SAVE = step(
  "save",
  "Save when ready",
  "Nothing is stored until you save or submit. You can leave and come back if a draft is kept.",
  "[data-tour='save']",
  "Look for Save or Submit at the bottom of the form."
);

const PIVOT_BUTTON = step(
  "pivot-button",
  "Add a pivot table",
  "When this table has rows, Add Pivot Table opens a builder. Drag fields into rows, columns, and values, then pin the layout to a dashboard.",
  "[data-tour='pivot-button']",
  "Select a program and wait for the table to load. Add Pivot Table appears above the rows."
);

export const PIVOT_TOUR = [
  step(
    "pivot-builder",
    "Arrange the table",
    "Drag fields into Rows, Columns, and Values. The grid on the right updates as you drop them. Use Add Calculated Field in the header if you need a formula.",
    "[data-tour='pivot-builder']",
    "The builder appears after Add Pivot Table opens."
  ),
  step(
    "pivot-add-dashboard",
    "Pin it for others",
    "Add to Dashboard opens the save panel. That is how this layout shows up on a program dashboard or on Reporting.",
    "[data-tour='pivot-add-dashboard']",
    "Look for Add to Dashboard in the pivot window header."
  ),
  step(
    "pivot-name",
    "Name the table",
    "This name is what staff see on the dashboard card. Keep it short and specific to the layout.",
    "[data-tour='pivot-name']",
    "Open Add to Dashboard to name the table."
  ),
  step(
    "pivot-destination",
    "Choose where it appears",
    "Dashboard shows it on that program’s page. Reporting Section shows it on Reporting. It will not appear on other programs.",
    "[data-tour='pivot-destination']",
    "Open Add to Dashboard to pick Dashboard or Reporting, then the program."
  ),
  step(
    "pivot-save",
    "Save the layout",
    "Save stores this pivot. Nothing is pinned until you save.",
    "[data-tour='pivot-save']",
    "Open Add to Dashboard, then Save at the end of the panel."
  ),
];

export const PAGE_TOURS = {
  "/dashboard": [
    step(
      "user",
      "This is you",
      "Your username stays here on every page. Open it to log out, or replay this walkthrough.",
      "[data-tour='user-menu']"
    ),
    step(
      "program",
      "Choose a program",
      "Pick a program to see live metrics. That is the first useful screen in this MIS.",
      "[data-tour='program-select']"
    ),
    step(
      "metrics",
      "Read the numbers",
      "Each card is a sticker assigned to this program in Dynamic Analytics. Click one to see the rows behind it.",
      "[data-tour='metrics']",
      "Choose a program first and the metric cards will appear here."
    ),
    step(
      "dates",
      "Set the period",
      "From and To change every number on this page and the PDF you download.",
      "[data-tour='date-range']",
      "Date range appears after a program is selected."
    ),
    step(
      "filters",
      "Slice the view",
      "Filters narrow by location and category. Applied filters show as chips under the button.",
      "[data-tour='filters']",
      "Filters appear after a program is selected."
    ),
    step(
      "print",
      "Take it with you",
      "Download this exact view as a PDF once the dashboard has finished loading.",
      "[data-tour='print']"
    ),
  ],
  "/reporting": [
    PAGE_INTRO,
    PROGRAM,
    step(
      "dates",
      "Set the period",
      "The date range applies to stickers, charts, and pivot tables on this reporting view.",
      "[data-tour='date-range']",
      "Date range appears after a program is selected."
    ),
    step(
      "filters",
      "Slice the view",
      "Filters narrow reporting by location and category. Applied filters show as chips under the button.",
      "[data-tour='filters']",
      "Filters appear after a program is selected."
    ),
    step(
      "print",
      "Export the page",
      "Download this reporting view as a PDF after the tables have finished loading.",
      "[data-tour='print']"
    ),
  ],
  "/dynamic-analytics": [
    PAGE_INTRO,
    TABS,
    step(
      "name",
      "Name the sticker",
      "This name is what staff see on the dashboard metric card.",
      "[data-tour='analytics-name']"
    ),
    step(
      "assign",
      "Pick the dashboard",
      "Target Dashboard and program decide where this sticker appears. It will not show on other programs.",
      "[data-tour='analytics-assign']"
    ),
    step(
      "steps",
      "Build the calculation",
      "Each step counts or compares a field. Chain steps if the sticker needs more than one operation.",
      "[data-tour='analytics-steps']"
    ),
    SAVE,
  ],
  "/dynamic-charts": [
    PAGE_INTRO,
    TABS,
    step(
      "type",
      "Choose a chart type",
      "Bar, pie, line, and pivot all use the same field pickers. Pivot stays collapsed on the dashboard until opened.",
      "[data-tour='chart-type']"
    ),
    step(
      "fields",
      "Map the axes",
      "Label is the category on the chart. Value is what gets counted or summed.",
      "[data-tour='chart-fields']"
    ),
    step(
      "assign",
      "Assign the dashboard",
      "The selected program dashboard is the only place this chart will render.",
      "[data-tour='chart-assign']"
    ),
    SAVE,
  ],
  "/add-data": [
    PAGE_INTRO,
    PROGRAM,
    TABS,
    FORM,
    SAVE,
  ],
  "/view-entry-data": [
    PAGE_INTRO,
    PROGRAM,
    TABS,
    step(
      "table",
      "Read and export rows",
      "This table is the saved data. Use column controls, filters, and export from the toolbar above it.",
      "[data-tour='table']",
      "Select a program to load the table."
    ),
    PIVOT_BUTTON,
  ],
  "/addprogram": [PAGE_INTRO, TABS, FORM, SAVE],
  "/add-sub-program": [PAGE_INTRO, TABS, FORM, SAVE],
  "/add-fields": [PAGE_INTRO, PROGRAM, TABS, FORM],
  "/add-subprogram-fields": [PAGE_INTRO, PROGRAM, FORM],
  "/add-partner": [PAGE_INTRO, TABS, FORM, SAVE],
  "/partner-form": [PAGE_INTRO, PROGRAM, TABS, FORM, SAVE],
  "/events": [PAGE_INTRO, PROGRAM, FORM],
  "/attendance": [
    PAGE_INTRO,
    step(
      "date",
      "Pick the date",
      "Attendance is stored for this date. Change it before marking present or absent.",
      "[data-tour='attendance-date']"
    ),
    PROGRAM,
    FORM,
  ],
  "/group-profile": [PAGE_INTRO, PROGRAM, TABS, FORM, SAVE, PIVOT_BUTTON],
  "/subgroup-profile": [PAGE_INTRO, PROGRAM, TABS, FORM, SAVE, PIVOT_BUTTON],
  "/public-form": [PAGE_INTRO, PROGRAM, TABS, FORM, SAVE],
  "/adduser": [PAGE_INTRO, TABS, FORM, SAVE],
  "/addrole": [PAGE_INTRO, TABS, FORM, SAVE],
  "/addproject": [PAGE_INTRO, FORM, SAVE],
  "/add-analytics": [PAGE_INTRO, PROGRAM, TABS, FORM],
  "/view-entry-data/add-record": [PAGE_INTRO, FORM, SAVE],
  "/view-entry-data/add-ben-record": [PAGE_INTRO, FORM, SAVE],
};

export const DEFAULT_PAGE_TOUR = [
  PAGE_INTRO,
  step(
    "nav",
    "Move around",
    "Every page is listed in this sidebar. Open a group, then pick a page.",
    "[data-tour='nav']"
  ),
  step(
    "user",
    "Account menu",
    "Open your name in the top right to replay this walkthrough or log out.",
    "[data-tour='user-menu']"
  ),
];

export const tourForPath = (pathname, variant) => {
  if (variant === PIVOT_TOUR_VARIANT) return PIVOT_TOUR;
  if (!pathname) return DEFAULT_PAGE_TOUR;
  return PAGE_TOURS[pathname] || DEFAULT_PAGE_TOUR;
};
